import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  CircularProgress,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { WhatsApp, ExpandMore, ExpandLess, EventAvailable, Print, FileDownload, Undo, Folder, HelpOutline } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ConfirmModal from "../components/modals/ConfirmModal";
import AppointmentModal, { AppointmentData } from "../components/modals/AppointmentModal";
import { schedulesService, mapScheduleError, ScheduleItem, ScheduleOptions } from "../services/schedulesService";
import { scheduleBlocksService } from "../services/scheduleBlocksService";
import { holidaysService } from "../services/holidaysService";
import { patientsService } from "../services/patientsService";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useSchedulePermissions } from "../hooks/useSchedulePermissions";
import { colors, typography } from "../theme/designSystem";
import { patientRegisterUrl } from "../utils/patientRegisterUrl";
import {
  opportunitiesService,
  QueueSituation,
  OpportunityQueue,
  OpportunityReport,
  OpportunityCard,
  VacancyCard,
  OpportunityType,
  OPPORTUNITY_TYPE_LABELS,
  RepositionQueue,
  RepositionCard,
  RepositionSuggestion,
  REPOSITION_RESOLUTION_LABELS,
} from "../services/opportunitiesService";

const TYPE_COLORS: Record<OpportunityType, { bg: string; fg: string }> = {
  holiday_relocation: { bg: "#f8d7da", fg: "#842029" },
  professional_cancellation: { bg: "#ffe5d0", fg: "#8a4b08" },
  double_session: { bg: "#e2d9f3", fg: "#59359a" },
  anticipation: { bg: "#cfe2ff", fg: "#0a58ca" },
};

const fmtDay = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const fmtTime = (iso: string) => iso.slice(11, 16);
const WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const weekdayOf = (iso: string) =>
  WEEKDAY_SHORT[new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))).getDay()];
// Identificador legível da vaga, como as pessoas se referem ao caso ("a OP-0000012")
const opCode = (n?: number) => `OP-${String(n ?? 0).padStart(7, "0")}`;
// Reposições (RN-409f) têm numeração própria: RP-0000001
const rpCode = (n?: number) => `RP-${String(n ?? 0).padStart(7, "0")}`;

// dd/mm/aaaa para as janelas do motor no cabeçalho
const fmtFullDay = (iso?: string | null) =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";

const money = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

// Frase humana da sugestão — a fila fala a língua da recepção, não a do banco
const suggestionPhrase = (card: VacancyCard, o: OpportunityCard): string => {
  const when = o.candidateDate ? `${fmtDay(o.candidateDate)}` : "";
  if (o.type === "holiday_relocation") {
    return `${o.patientName} tem sessão marcada em feriado (${when}) — será perdida se não for realocada. Oferecer ${fmtDay(card.startsAt)} ${fmtTime(card.startsAt)}.`;
  }
  if (o.type === "professional_cancellation") {
    // nomeia quem cancelou: sem isso a recepção não sabe de qual terapia é a reposição
    const quem = o.candidateProfessionalName ? ` com ${o.candidateProfessionalName}` : "";
    const servico = o.candidateServiceName ? ` (${o.candidateServiceName})` : "";
    return `${o.patientName} teve a sessão de ${when}${quem}${servico} cancelada pelo profissional — oferecer ${fmtDay(card.startsAt)} ${fmtTime(card.startsAt)}${card.professionalName ? ` com ${card.professionalName}` : ""}, que também o atende.`;
  }
  if (o.type === "double_session") {
    const at = o.candidateDate ? `às ${fmtTime(o.candidateDate)}` : "no mesmo dia";
    const ctx = [o.candidateServiceName, o.candidateProfessionalName].filter(Boolean).join(" · ");
    return `${o.patientName} já estará na clínica ${at}${ctx ? ` (${ctx})` : ""} — emenda direta, a família não faz viagem extra.`;
  }
  return `${o.patientName} tem sessão em ${when} — antecipar para ${fmtDay(card.startsAt)} ${fmtTime(card.startsAt)} encurta o tratamento e libera o horário futuro.`;
};

const waLink = (o: OpportunityCard, card: VacancyCard): string | null => {
  if (!o.patientPhone) return null;
  const msg = `Olá! Surgiu um horário em ${fmtDay(card.startsAt)} às ${fmtTime(card.startsAt)}${card.professionalName ? ` com ${card.professionalName}` : ""}. Podemos confirmar para você?`;
  return `https://wa.me/${o.patientPhone}?text=${encodeURIComponent(msg)}`;
};

// Reposição: a mensagem já leva a melhor sugestão calculada, se houver
const waRepositionLink = (card: RepositionCard): string | null => {
  if (!card.patientPhone) return null;
  const best = card.sameTime[0] ?? card.sameProfessional[0] ?? card.crossProfessionals[0];
  const oferta = best
    ? ` Podemos repor em ${fmtDay(best.startsAt)} às ${fmtTime(best.startsAt)} com ${best.professionalName}?`
    : " Podemos combinar uma reposição?";
  const msg = `Olá! A sessão de ${fmtDay(card.lostStartsAt)} às ${fmtTime(card.lostStartsAt)}${card.professionalName ? ` com ${card.professionalName}` : ""} precisou ser cancelada pelo profissional.${oferta}`;
  return `https://wa.me/${card.patientPhone}?text=${encodeURIComponent(msg)}`;
};

const Opportunities: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  // Aba pedida na URL (?tab=repositions): o widget do Dashboard aponta direto para a reposição
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "repositions" ? 1 : 0);
  const [queue, setQueue] = useState<OpportunityQueue | null>(null);
  const [repositions, setRepositions] = useState<RepositionQueue | null>(null);
  const [repositionRulesOpen, setRepositionRulesOpen] = useState(false);
  const [report, setReport] = useState<OpportunityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [professionalFilter, setProfessionalFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dismissTarget, setDismissTarget] = useState<VacancyCard | null>(null);
  const [dismissRepositionTarget, setDismissRepositionTarget] = useState<RepositionCard | null>(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [situation, setSituation] = useState<QueueSituation>("open");
  const [reopenTarget, setReopenTarget] = useState<
    { card: VacancyCard; offer?: OpportunityCard } | null
  >(null);
  const [reopenReason, setReopenReason] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());

  // Filtro de período do Relatório (pedido do Hilton, 21/08): default últimos 30 dias
  const isoDay = (d: Date) => d.toISOString().slice(0, 10);
  const [reportFrom, setReportFrom] = useState(() => isoDay(new Date(Date.now() - 30 * 86400000)));
  const [reportTo, setReportTo] = useState(() => isoDay(new Date()));

  const load = async (silent = false) => {
    if (!entityId) return;
    // Ação sobre um card recarrega os dados em silêncio: sem spinner, a tela não "pisca".
    if (!silent) setLoading(true);
    try {
      const [q, rp, r] = await Promise.all([
        opportunitiesService.getQueue(entityId, situation),
        opportunitiesService.getRepositions(entityId, situation),
        opportunitiesService.getReport(entityId, reportFrom, reportTo),
      ]);
      setQueue(q);
      setRepositions(rp);
      setReport(r);
    } catch {
      showToast("Erro ao carregar as oportunidades.", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Oportunidades de agenda";
    orderRef.current = [];   // filtro/situação novos: reordena por score
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, situation]);

  // Período do Relatório mudou: recarrega só o report, em silêncio
  useEffect(() => {
    if (!entityId) return;
    opportunitiesService.getReport(entityId, reportFrom, reportTo)
      .then(setReport)
      .catch(() => showToast("Erro ao carregar o relatório.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFrom, reportTo]);

  // contagem regressiva dos holds
  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const professionals = useMemo(
    () => Array.from(new Set((queue?.cards ?? []).map((c) => c.professionalName).filter(Boolean))) as string[],
    [queue]
  );

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    (queue?.cards ?? []).forEach((c) => {
      counts.all++;
      if (c.recommended) counts[c.recommended.type] = (counts[c.recommended.type] ?? 0) + 1;
    });
    return counts;
  }, [queue]);

  // A ordem é definida quando a lista chega e fica CONGELADA enquanto se trabalha nela:
  // agir num card não pode fazê-lo pular de posição (pedido do Hilton, 03/08/2026).
  // Trocar de filtro/situação ou recarregar a página reordena por score.
  const orderRef = useRef<string[]>([]);

  const cards = useMemo(() => {
    const visible = (queue?.cards ?? [])
      .filter((c) => typeFilter === "all" || c.recommended?.type === typeFilter)
      .filter((c) => professionalFilter === "all" || c.professionalName === professionalFilter);

    const known = new Map(orderRef.current.map((id, i) => [id, i]));
    const ranked = [...visible].sort((a, b) => {
      const ia = known.get(a.vacancyId);
      const ib = known.get(b.vacancyId);
      if (ia !== undefined && ib !== undefined) return ia - ib;
      if (ia !== undefined) return -1;   // já estava na tela: mantém acima dos novos
      if (ib !== undefined) return 1;
      return (b.recommended?.score ?? -1) - (a.recommended?.score ?? -1);
    });

    orderRef.current = ranked.map((c) => c.vacancyId);
    return ranked;
  }, [queue, typeFilter, professionalFilter]);

  // Impressão e exportação agem sobre a aba aberta; a fila leva os filtros da tela.
  const handlePrint = () => window.print();

  const handleExport = async () => {
    if (!entityId || exporting) return;
    setExporting(true);
    try {
      const isQueue = tab === 0;
      const blob = isQueue
        ? await opportunitiesService.exportQueueXlsx(entityId, typeFilter, professionalFilter)
        : await opportunitiesService.exportReportXlsx(entityId, reportFrom, reportTo);
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `oportunidades_${isQueue ? "fila" : "relatorio"}_${today}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast("Não foi possível exportar a planilha. Tente novamente.", "error");
    } finally {
      setExporting(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      showToast(ok, "success");
      await load(true);
    } catch (e: any) {
      showToast(e?.response?.data?.description || "A ação não pôde ser concluída — atualize a fila.", "error");
      await load(true);
    } finally {
      setBusy(false);
    }
  };

  const holdMinutesLeft = (o: OpportunityCard): number | null => {
    if (o.status !== "held" || !o.holdExpiresAt) return null;
    return Math.max(0, Math.round((new Date(o.holdExpiresAt).getTime() - nowTick) / 60000));
  };

  const repositionHoldMinutesLeft = (card: RepositionCard): number | null => {
    if (card.status !== "held" || !card.holdExpiresAt) return null;
    return Math.max(0, Math.round((new Date(card.holdExpiresAt).getTime() - nowTick) / 60000));
  };

  // ---- RN-409i: efetivação do aceite/reposição pelo próprio modal da agenda ----
  // Sem permissão de agenda, os botões mantêm o comportamento antigo (só registram o
  // resultado; a reconciliação do sync fecha o vínculo quando alguém efetivar por fora).
  // Efetivar reposição cria ou move agendamento (RN-409i): exige inserir ou editar (RN-115d)
  const oppSchedulePerms = useSchedulePermissions();
  const canEditSchedule = oppSchedulePerms.canCreate || oppSchedulePerms.canEdit;
  const [scheduleOptions, setScheduleOptions] = useState<ScheduleOptions | null>(null);
  const [apptModalOpen, setApptModalOpen] = useState(false);
  const [apptModalMode, setApptModalMode] = useState<"create" | "edit">("create");
  const [apptModalData, setApptModalData] = useState<Partial<AppointmentData>>({});
  const [apptPatient, setApptPatient] = useState<{ id: string; name: string } | null>(null);
  // edição: espelho do gravado (pago/travado alimentam os avisos do modal)
  const [apptSchedule, setApptSchedule] = useState<ScheduleItem | null>(null);
  // edição: o horário da vaga entra como MUDANÇA PENDENTE por cima do gravado — sem isso
  // o dirty-check do modal via o form "limpo" e o Salvar nascia desabilitado
  const [apptPrefill, setApptPrefill] = useState<Partial<AppointmentData> | undefined>(undefined);
  const [apptContext, setApptContext] = useState<
    | { kind: "vacancy"; opportunityId: string; editScheduleId: string | null }
    | { kind: "reposition"; repositionId: string }
    | null
  >(null);

  const ensureScheduleOptions = async (): Promise<ScheduleOptions> => {
    if (scheduleOptions) return scheduleOptions;
    const opts = await schedulesService.getOptions();
    setScheduleOptions(opts);
    return opts;
  };

  // RN-118j na efetivação: serviço de pacote criaria N sessões num fluxo que por definição
  // preenche UM horário (decisão do Hilton, 21/08) — fica fora do combo. O serviço GRAVADO
  // da sessão em edição permanece mesmo sendo pacote (não é troca). Pacote resolve por id
  // (o catálogo tem nomes duplicados).
  const isPackageServiceId = (opts: ScheduleOptions, serviceTypeId?: string | null) =>
    ((opts.serviceTypes.find((s) => s.id === serviceTypeId)?.packageAmount) ?? 1) > 1;
  const effectuationServiceTypes = useMemo(() => {
    const all = scheduleOptions?.serviceTypes ?? [];
    const keep = apptModalMode === "edit" ? apptSchedule?.serviceTypeName : null;
    return all.filter((s) => (s.packageAmount ?? 1) <= 1 || s.name === keep);
  }, [scheduleOptions, apptModalMode, apptSchedule]);

  const closeEffectuationModal = () => {
    setApptModalOpen(false);
    setApptContext(null);
    setApptPatient(null);
    setApptSchedule(null);
    setApptModalData({});
    setApptPrefill(undefined);
  };

  // Aceite de vaga: antecipação/feriado têm sessão futura para MOVER (modal de edição no
  // horário da vaga); dobradinha/realocação não têm — o modal de criação preenche a vaga.
  const openEffectuationForVacancy = async (card: VacancyCard, o: OpportunityCard) => {
    setBusy(true);
    try {
      const opts = await ensureScheduleOptions();
      const vacancyProfessionalName = (card.professionalId
        ? opts.professionals.find((p) => p.id === card.professionalId)?.name
        : null) ?? card.professionalName ?? "";
      const moveScheduleId =
        (o.type === "anticipation" || o.type === "holiday_relocation") && o.candidateScheduleId
          ? o.candidateScheduleId
          : null;
      if (moveScheduleId) {
        const item = await schedulesService.getById(moveScheduleId);
        const money = (v: number | null): string => (v == null ? "" : v.toFixed(2));
        setApptSchedule(item);
        // initialData = o GRAVADO; o horário da vaga entra como override pendente
        setApptModalData({
          patient: item.patientName ?? o.patientName,
          firstResponsible: "",
          secondResponsible: "",
          professional: item.professionalName ?? "",
          team: item.teamName || "",
          serviceType: item.serviceTypeName || "",
          startDate: item.dateTimeInitial.slice(0, 10),
          startTime: item.dateTimeInitial.slice(11, 16),
          endDate: item.dateTimeInitial.slice(0, 10),
          endTime: item.dateTimeFinal.slice(11, 16),
          appointmentType: "unique",   // a efetivação move SÓ esta ocorrência
          recurrenceType: "",
          maxOccurrences: item.occurrences ?? 1,
          unitValue: money(item.eventValueUnit),
          discount: money(item.eventValueUnitDiscount ?? 0),
          totalUnit: money(item.eventValueTotalUnit),
          totalValue: money(item.eventValueTotalAll),
          observations: item.notes || "",
          confirmationStatus: item.patientConfirmation || "",
          attendanceStatus: item.patientPresence || "",
          cancellationReason: item.reasonToCancellation || "",
        });
        const sameProfessional = !card.professionalId || card.professionalId === item.professionalId;
        setApptPrefill({
          startDate: card.startsAt.slice(0, 10),
          startTime: fmtTime(card.startsAt),
          endDate: card.startsAt.slice(0, 10),
          endTime: fmtTime(card.endsAt),
          // Profissional da vaga difere do da sessão: troca e deixa a equipe para o usuário
          // confirmar (o modal restringe pelas equipes do profissional — RN-115)
          ...(sameProfessional ? {} : { professional: vacancyProfessionalName, team: "" }),
        });
        setApptModalMode("edit");
        setApptContext({ kind: "vacancy", opportunityId: o.id, editScheduleId: item.id });
      } else {
        setApptSchedule(null);
        setApptPrefill(undefined);
        setApptModalData({
          patient: o.patientName,
          firstResponsible: "",
          secondResponsible: "",
          professional: vacancyProfessionalName,
          team: "",
          // Serviço da vaga sendo pacote fica de fora do combo — prefill vazio
          serviceType: isPackageServiceId(opts, card.serviceTypeId) ? "" : card.serviceName ?? "",
          serviceTypeId: isPackageServiceId(opts, card.serviceTypeId) ? null : card.serviceTypeId ?? null,
          startDate: card.startsAt.slice(0, 10),
          startTime: fmtTime(card.startsAt),
          endDate: card.startsAt.slice(0, 10),
          endTime: fmtTime(card.endsAt),
          appointmentType: "unique",
          recurrenceType: "",
          maxOccurrences: 1,
          unitValue: "",
          discount: "",
          totalUnit: "",
          totalValue: "",
          observations: "",
        });
        setApptModalMode("create");
        setApptContext({ kind: "vacancy", opportunityId: o.id, editScheduleId: null });
      }
      setApptPatient({ id: o.patientId, name: o.patientName });
      setApptModalOpen(true);
    } catch (e: any) {
      showToast(mapScheduleError(e), "error");
    } finally {
      setBusy(false);
    }
  };

  // Reposição: sempre CRIA um agendamento novo; a sugestão clicada pré-preenche o horário
  const openEffectuationForReposition = async (card: RepositionCard, slot?: RepositionSuggestion) => {
    setBusy(true);
    try {
      const opts = await ensureScheduleOptions();
      const professionalId = slot?.professionalId ?? card.professionalId ?? null;
      const professionalName = (professionalId
        ? opts.professionals.find((p) => p.id === professionalId)?.name
        : null) ?? slot?.professionalName ?? card.professionalName ?? "";
      const serviceName = isPackageServiceId(opts, card.serviceTypeId)
        ? ""   // serviço perdido era pacote: fica de fora do combo — usuário escolhe
        : ((card.serviceTypeId
          ? opts.serviceTypes.find((s) => s.id === card.serviceTypeId)?.name
          : null) ?? card.serviceName ?? "");
      setApptSchedule(null);
      setApptPrefill(undefined);
      setApptModalData({
        patient: card.patientName,
        firstResponsible: "",
        secondResponsible: "",
        professional: professionalName,
        team: "",
        serviceType: serviceName,
        serviceTypeId: serviceName ? card.serviceTypeId ?? null : null,
        startDate: slot ? slot.startsAt.slice(0, 10) : "",
        startTime: slot ? fmtTime(slot.startsAt) : "08:00",
        endDate: slot ? slot.startsAt.slice(0, 10) : "",
        endTime: slot ? fmtTime(slot.endsAt) : "09:00",
        appointmentType: "unique",
        recurrenceType: "",
        maxOccurrences: 1,
        unitValue: "",
        discount: "",
        totalUnit: "",
        totalValue: "",
        observations: `Reposição da sessão de ${fmtDay(card.lostStartsAt)} às ${fmtTime(card.lostStartsAt)} (${rpCode(card.number)})`,
      });
      setApptModalMode("create");
      setApptContext({ kind: "reposition", repositionId: card.id });
      setApptPatient({ id: card.patientId, name: card.patientName });
      setApptModalOpen(true);
    } catch (e: any) {
      showToast(mapScheduleError(e), "error");
    } finally {
      setBusy(false);
    }
  };

  // Grava na agenda (RN-302, com todos os guards) e SÓ ENTÃO registra o resultado já
  // vinculado ao agendamento. Falha no registro não desfaz a agenda: avisa e deixa
  // registrar de novo pelo botão do card.
  const handleEffectuationSave = async (data: AppointmentData) => {
    if (!apptContext || !apptPatient) return;
    if (!data.professionalId || !data.serviceTypeId) {
      throw new Error("Selecione profissional e serviço.");
    }
    if (!data.startDate || !data.startTime || !data.endTime) {
      throw new Error("Informe a data e os horários do agendamento.");
    }

    let scheduleId: string;
    try {
      if (apptContext.kind === "vacancy" && apptContext.editScheduleId) {
        await schedulesService.updateSchedule(apptContext.editScheduleId, {
          professionalRegistrationId: data.professionalId,
          teamId: data.teamId ?? null,
          serviceTypeId: data.serviceTypeId,
          startDate: data.startDate.slice(0, 10),
          startTime: data.startTime,
          endTime: data.endTime,
          confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
          presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
          notes: data.observations ? data.observations : null,
          cancellationReason: data.cancellationReason ? data.cancellationReason : null,
          applyToSeries: false,
        });
        scheduleId = apptContext.editScheduleId;
      } else {
        const result = await schedulesService.createSchedule({
          patientId: apptPatient.id,
          professionalRegistrationId: data.professionalId,
          teamId: data.teamId ?? null,
          serviceTypeId: data.serviceTypeId,
          startDate: data.startDate.slice(0, 10),
          startTime: data.startTime,
          endTime: data.endTime,
          recurring: false,
          recurrenceType: null,
          occurrences: 1,
          notes: data.observations ? data.observations : null,
          confirmationStatus: data.confirmationStatus ? data.confirmationStatus : null,
          presenceStatus: data.attendanceStatus ? data.attendanceStatus : null,
          skippedDates: [],
        });
        scheduleId = result.ids[0];
      }
    } catch (error: any) {
      throw new Error(mapScheduleError(error));
    }

    try {
      if (apptContext.kind === "vacancy") {
        await opportunitiesService.resolve(
          apptContext.opportunityId, true, "whatsapp", undefined, scheduleId);
      } else {
        await opportunitiesService.resolveReposition(
          apptContext.repositionId, true, undefined, scheduleId);
      }
    } catch {
      showToast(
        "Agendamento gravado, mas o registro do resultado falhou — registre pelo botão do card.",
        "error");
      closeEffectuationModal();
      await load(true);
      return;
    }

    showToast(
      apptContext.kind === "vacancy"
        ? "Agendamento gravado e aceite registrado — vaga preenchida."
        : "Reposição agendada e registrada.",
      "success");
    closeEffectuationModal();
    await load(true);
  };

  // Loaders do modal com o paciente JÁ resolvido por id (o campo fica desabilitado)
  const loadPatientDiscountsById = React.useCallback(async (_name: string) => {
    return apptPatient ? schedulesService.getPatientDiscounts(apptPatient.id) : [];
  }, [apptPatient]);

  const loadPatientInfoByName = React.useCallback(async (patientName: string) => {
    const result = await patientsService.getAll({
      pageNumber: 1, pageSize: 5, search: patientName, status: "all", test: "all",
    });
    const patient = result.items.find((p) => p.patientName === patientName) ?? null;
    if (!patient) return null;
    return {
      responsible1: patient.responsibleName1 ?? null,
      responsible2: patient.responsibleName2 ?? null,
    };
  }, []);

  const loadHolidaysOnDate = React.useCallback(async (date: string) => {
    if (!entityId) return [];
    const rows = await holidaysService.getOnDate(entityId, date);
    return rows.map((h) => h.holidayTitle);
  }, [entityId]);

  // Sugestões em chips: dia da semana + data + hora falam a língua da recepção.
  // Com permissão de agenda, o chip agenda a sugestão direto no modal (RN-409i).
  const suggestionChips = (card: RepositionCard, slots: RepositionSuggestion[], withName: boolean) => {
    const clickable = canEditSchedule
      && (card.status === "open" || card.status === "held")
      && !card.inContactOnVacancyNumber;
    return slots.map((s, i) => (
      <Chip
        key={`${s.professionalId}-${s.startsAt}-${i}`}
        size="small"
        label={`${weekdayOf(s.startsAt)} ${fmtDay(s.startsAt)} · ${fmtTime(s.startsAt)}${withName ? ` · ${s.professionalName}` : ""}`}
        onClick={clickable ? () => openEffectuationForReposition(card, s) : undefined}
        title={clickable ? "Agendar esta sugestão pelo modal da agenda" : undefined}
        sx={{ backgroundColor: "#e8f4f4", color: "#0d6e6e", fontWeight: 600 }}
      />
    ));
  };

  const renderRepositionCard = (card: RepositionCard) => {
    const workable = card.status === "open" || card.status === "held";
    const lockedByOther = workable && !!card.inContactOnVacancyNumber;
    const minutes = repositionHoldMinutesLeft(card);
    const wa = waRepositionLink(card);
    const noSuggestion =
      card.sameTime.length === 0 && card.sameProfessional.length === 0 && card.crossProfessionals.length === 0;
    return (
      <Paper key={card.id} className="opportunity-card" elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "nowrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip label="Reposição" size="small" sx={{ backgroundColor: "#d1e7dd", color: "#0f5132", fontWeight: 700 }} />
            {card.value != null && card.value > 0 && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{money(card.value)} a repor</Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            title="Identificador desta reposição"
            sx={{ fontFamily: "monospace", color: colors.textSecondary, bgcolor: "#f1f3f5", px: 0.75, py: 0.25, borderRadius: "4px" }}
          >
            {rpCode(card.number)}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 700, color: colors.textPrimary, mt: 0.5 }}>
          {card.patientName}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", mb: 1 }}>
          Sessão de {weekdayOf(card.lostStartsAt)} {fmtDay(card.lostStartsAt)} às {fmtTime(card.lostStartsAt)}
          {card.professionalName ? ` com ${card.professionalName}` : ""}
          {card.serviceName ? ` (${card.serviceName})` : ""} cancelada pelo profissional — a clínica deve a reposição.
        </Typography>

        {!workable && (
          <Box sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: "6px", p: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {card.status === "resolved" ? "Resolvida" : card.status === "dismissed" ? "Dispensada" : "Expirada"}
              {card.actedAt ? ` em ${fmtDay(card.actedAt)} ${fmtTime(card.actedAt)}` : ""}
              {card.status === "resolved" && card.resolutionKind
                ? ` · ${REPOSITION_RESOLUTION_LABELS[card.resolutionKind] ?? card.resolutionKind}`
                : ""}
              {card.notes ? ` — ${card.notes}` : ""}
            </Typography>
          </Box>
        )}

        {workable && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1 }}>
            {card.sameTime.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                  Mesmo horário, outro profissional que já o atende:
                </Typography>
                {suggestionChips(card, card.sameTime, true)}
              </Box>
            )}
            {card.sameProfessional.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                  Outro horário com {card.professionalName || "o mesmo profissional"}:
                </Typography>
                {suggestionChips(card, card.sameProfessional, false)}
              </Box>
            )}
            {card.crossProfessionals.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                  Outro horário com quem já o atende:
                </Typography>
                {suggestionChips(card, card.crossProfessionals, true)}
              </Box>
            )}
            {noSuggestion && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                Sem horário livre encontrado no horizonte de sugestão — combine manualmente com a família.
              </Typography>
            )}
          </Box>
        )}

        {workable && lockedByOther && (
          <Chip
            className="no-print"
            size="small"
            label={`Em contato na ${opCode(card.inContactOnVacancyNumber || 0)}${card.inContactByName ? ` · ${card.inContactByName}` : ""}`}
            title="Este paciente está sendo contatado para uma vaga da fila — aguarde o resultado ou o vencimento da reserva antes de falar de reposição."
            sx={{ backgroundColor: "#fff3cd", color: "#664d03", fontWeight: 600 }}
          />
        )}

        {workable && !lockedByOther && (
          <Box className="no-print" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              {card.hoursWithoutAction >= 24 && card.status === "open" && (
                <Typography variant="caption" sx={{ bgcolor: "#fff3cd", color: "#664d03", px: 1, py: 0.25, borderRadius: "999px", fontWeight: 600 }}>
                  sem ação há {Math.floor(card.hoursWithoutAction)}h
                </Typography>
              )}
              {minutes !== null && (
                <Typography variant="caption" sx={{ bgcolor: "#cfe2ff", color: "#0a58ca", px: 1, py: 0.25, borderRadius: "999px", fontWeight: 600 }}>
                  em contato{card.heldByName ? ` por ${card.heldByName}` : ""} · reserva expira em {minutes} min
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Abrir cadastro do paciente em nova aba">
                <IconButton
                  component="a"
                  href={patientRegisterUrl(card.patientId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }}
                >
                  <Folder fontSize="small" />
                </IconButton>
              </Tooltip>
              {card.status === "open" ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={() => act(() => opportunitiesService.holdReposition(card.id), "Reserva registrada — bom contato!")}
                  sx={{ textTransform: "none", backgroundColor: "#0d6e6e", "&:hover": { backgroundColor: "#0a5757" } }}
                >
                  Vou contatar
                </Button>
              ) : (
                <>
                  {wa && (
                    <Button size="small" variant="outlined" startIcon={<WhatsApp />} href={wa} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", color: "#128c7e", borderColor: "#128c7e" }}>
                      WhatsApp
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy}
                    onClick={() => canEditSchedule
                      ? openEffectuationForReposition(card)
                      : act(() => opportunitiesService.resolveReposition(card.id, true), "Reposição combinada — efetive o agendamento na agenda.")}
                    title={canEditSchedule ? "Abre o modal da agenda para criar a reposição (clicar numa sugestão preenche o horário)" : undefined}
                    sx={{ textTransform: "none", backgroundColor: "#28a745", "&:hover": { backgroundColor: "#218838" } }}
                  >
                    Combinado
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => act(() => opportunitiesService.resolveReposition(card.id, false), "Recusa registrada.")}
                    sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
                  >
                    Recusou
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => setDismissRepositionTarget(card)}
                sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
              >
                Dispensar
              </Button>
            </Box>
            {/* RN-409i: caminho antigo preservado — registra o combinado e deixa a efetivação
                por fora (a reconciliação do sync fecha o vínculo) */}
            {canEditSchedule && card.status === "held" && (
              <Typography
                variant="caption"
                component="button"
                disabled={busy}
                onClick={() => act(() => opportunitiesService.resolveReposition(card.id, true), "Reposição combinada — efetive o agendamento na agenda.")}
                sx={{ color: "#6c757d", textAlign: "right", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", p: 0, alignSelf: "flex-end" }}
              >
                registrar combinado sem mexer na agenda
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const renderRepositions = () => (
    <>
      <Box className="no-print" sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, alignItems: "center" }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary, flex: 1, minWidth: 240 }}>
          Sessões canceladas <strong>pelo profissional</strong> — repor é compromisso da clínica.
          Sugestões de horário livre calculadas agora, considerando disponibilidade, funcionamento,
          feriados e as agendas do profissional e do paciente
          {repositions?.suggestionHorizonEnd ? ` (até ${fmtFullDay(repositions.suggestionHorizonEnd)})` : ""}.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel shrink>Situação</InputLabel>
          <Select
            label="Situação"
            value={situation}
            onChange={(e) => setSituation(e.target.value as QueueSituation)}
            notched
            displayEmpty
          >
            <MenuItem value="open">Em aberto</MenuItem>
            <MenuItem value="resolved">Resolvidas</MenuItem>
            <MenuItem value="dismissed">Dispensadas</MenuItem>
            <MenuItem value="all">Todas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* RN-409j: a mecânica da reposição explicada na própria tela, sem depender de hover */}
      <Box className="no-print" sx={{ mb: 2 }}>
        <Button
          size="small"
          startIcon={<HelpOutline />}
          endIcon={repositionRulesOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setRepositionRulesOpen((v) => !v)}
          sx={{ textTransform: "none", fontWeight: 600, color: "#0d6e6e", px: 0.5 }}
        >
          Como o sistema controla as reposições
        </Button>
        <Collapse in={repositionRulesOpen}>
          <Paper elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2, mt: 1, backgroundColor: "#f8f9fa" }}>
            <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: "0.85rem", color: colors.textPrimary, "& li": { mb: 0.75 } }}>
              <li>
                <strong>Origem.</strong> Toda sessão marcada na agenda como <em>Cancelado profissional</em> vira uma
                reposição (RP-…) em até 1 hora. Cancelamento pelo paciente não gera reposição — vira vaga na aba
                "Vagas a preencher".
              </li>
              <li>
                <strong>Prazo.</strong> A reposição fica pendente por até {repositions?.suggestionHorizonDays || 30} dias
                após a data da sessão perdida; depois expira sozinha.
              </li>
              <li>
                <strong>Sugestões.</strong> Calculadas na hora, em ordem de menor atrito: mesmo horário com outro
                profissional que já atende o paciente; outro dia com o mesmo profissional; outro dia com quem já o atende.
              </li>
              <li>
                <strong>Como ela é fechada</strong> — três caminhos:
                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, "& li": { mb: 0.5 } }}>
                  <li>
                    <strong>Combinada pela equipe:</strong> botão "Combinado" no card, com ou sem criar o agendamento na hora.
                  </li>
                  <li>
                    <strong>Registrada pelo reagendamento:</strong> a sessão cancelada foi reagendada pela ação "Reagendar"
                    da agenda — o novo agendamento fica vinculado à sessão perdida e fecha a reposição sozinho, com
                    qualquer profissional ou serviço.
                  </li>
                  <li>
                    <strong>Detectada como sessão extra:</strong> a recepção criou por fora um agendamento novo para o
                    paciente. O sistema o reconhece como reposição se for uma sessão avulsa (não parte de uma série criada
                    de uma vez), criada depois do cancelamento, marcada entre o cancelamento e{" "}
                    {repositions?.suggestionHorizonDays || 30} dias após a sessão perdida, com o mesmo profissional, o
                    mesmo serviço ou outro profissional que já atende o paciente.
                  </li>
                </Box>
              </li>
              <li>
                <strong>O que não conta:</strong> série de sessões criada de uma vez (o plano normal do mês); agendamento
                no mesmo horário com o mesmo profissional que cancelou (é edição, não reposição); serviço sem cobrança por
                sessão; sessão fora do prazo.
              </li>
              <li>
                <strong>Cada agendamento fecha uma única reposição.</strong> Duas sessões canceladas exigem duas reposições.
              </li>
              <li>
                <strong>Reposição cancelada depois</strong> reabre a pendência sozinha.
              </li>
              <li>
                <strong>Caminho mais confiável:</strong> use "Reagendar" na agenda ou "Combinado" aqui — a reposição já
                nasce vinculada e o sistema não precisa adivinhar.
              </li>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      {(repositions?.cards ?? []).length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
          <EventAvailable sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
          <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
            {situation === "open" ? "Nenhuma reposição pendente" : "Nada por aqui"}
          </Typography>
          <Typography variant="body2">
            {situation === "open"
              ? "Cancelamentos feitos pelo profissional aparecem aqui em até 1 hora após o registro na agenda."
              : "Troque a situação no filtro para ver as demais reposições."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {(repositions?.cards ?? []).map(renderRepositionCard)}
        </Box>
      )}
    </>
  );

  const renderOpportunity = (card: VacancyCard, o: OpportunityCard, isRecommended: boolean) => {
    const wa = waLink(o, card);
    const minutes = holdMinutesLeft(o);
    // Vaga fora da fila (resolvida/descartada) ou oferta já tratada: só histórico, sem ações.
    const actionable =
      (card.status === "open" || card.status === "held") &&
      (o.status === "suggested" || o.status === "held");
    // Mesmo paciente reservado em outra vaga: não pode ser contatado em duplicidade
    const lockedByOther = !!o.inContactOnVacancyNumber && o.status !== "held";
    return (
      <Box key={o.id} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: { sm: "center" }, pt: isRecommended ? 0 : 1.5, mt: isRecommended ? 0 : 1.5, borderTop: isRecommended ? "none" : "1px dashed #e9ecef" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.9rem", color: colors.textSecondary }}>
            {suggestionPhrase(card, o)}
          </Typography>
          {o.hoursWithoutAction >= 24 && o.status === "suggested" && (
            <Typography variant="caption" sx={{ bgcolor: "#fff3cd", color: "#664d03", px: 1, py: 0.25, borderRadius: "999px", fontWeight: 600 }}>
              sem ação há {Math.floor(o.hoursWithoutAction)}h
            </Typography>
          )}
          {minutes !== null && (
            <Typography variant="caption" sx={{ bgcolor: "#cfe2ff", color: "#0a58ca", px: 1, py: 0.25, borderRadius: "999px", fontWeight: 600 }}>
              em contato{o.heldByName ? ` por ${o.heldByName}` : ""} · reserva expira em {minutes} min
            </Typography>
          )}
        </Box>
        {!actionable && o.status === "declined" && (
          <Box className="no-print" sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
            <Chip label="Recusada" size="small" sx={{ backgroundColor: "#e9ecef", color: "#495057", fontWeight: 600 }} />
            {o.canReopen && (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => setReopenTarget({ card, offer: o })}
                startIcon={<Undo />}
                sx={{ textTransform: "none", color: "#0d6e6e", borderColor: "#0d6e6e" }}
              >
                Reconsiderar
              </Button>
            )}
          </Box>
        )}
        {actionable && lockedByOther && (
          <Box className="no-print" sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <Chip
              size="small"
              label={`Em contato na ${opCode(o.inContactOnVacancyNumber || 0)}${o.inContactByName ? ` · ${o.inContactByName}` : ""}`}
              title={
                o.inContactByName
                  ? `Este paciente está sendo contatado para outra vaga por ${o.inContactByName} — fale com essa pessoa para saber o andamento, ou aguarde o vencimento da reserva.`
                  : "Este paciente está sendo contatado para outra vaga — aguarde o resultado ou o vencimento da reserva."
              }
              sx={{ backgroundColor: "#fff3cd", color: "#664d03", fontWeight: 600 }}
            />
          </Box>
        )}
        {actionable && !lockedByOther && (
        <Box className="no-print" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
          <Tooltip title="Abrir cadastro do paciente em nova aba">
            <IconButton
              component="a"
              href={patientRegisterUrl(o.patientId)}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }}
            >
              <Folder fontSize="small" />
            </IconButton>
          </Tooltip>
          {o.status === "suggested" ? (
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => act(() => opportunitiesService.hold(o.id), "Reserva registrada — bom contato!")}
              sx={{ textTransform: "none", backgroundColor: "#0d6e6e", "&:hover": { backgroundColor: "#0a5757" } }}
            >
              Vou contatar
            </Button>
          ) : (
            <>
              {wa && (
                <Button size="small" variant="outlined" startIcon={<WhatsApp />} href={wa} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", color: "#128c7e", borderColor: "#128c7e" }}>
                  WhatsApp
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => canEditSchedule
                  ? openEffectuationForVacancy(card, o)
                  : act(() => opportunitiesService.resolve(o.id, true, "whatsapp"), "Aceite registrado — efetive o reagendamento na agenda.")}
                title={canEditSchedule ? "Abre o modal da agenda já preenchido com o horário da vaga" : undefined}
                sx={{ textTransform: "none", backgroundColor: "#28a745", "&:hover": { backgroundColor: "#218838" } }}
              >
                Aceitou
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => act(() => opportunitiesService.resolve(o.id, false), "Recusa registrada — o próximo candidato sobe na fila.")}
            sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}
          >
            {o.status === "held" ? "Recusou" : "Recusar sugestão"}
          </Button>
        </Box>
        {/* Quem está tratando: sem isso ninguém sabe a quem perguntar o andamento antes de registrar
            aceite ou recusa. */}
        {o.status === "held" && o.heldByName && (
          <Typography variant="caption" sx={{ color: "#6c757d", textAlign: "right" }}>
            tratativa de {o.heldByName}
          </Typography>
        )}
        {/* RN-409i: caminho antigo preservado — registra o aceite e deixa a efetivação por fora
            (a reconciliação do sync fecha o vínculo) */}
        {canEditSchedule && o.status === "held" && (
          <Typography
            variant="caption"
            component="button"
            disabled={busy}
            onClick={() => act(() => opportunitiesService.resolve(o.id, true, "whatsapp"), "Aceite registrado — efetive o reagendamento na agenda.")}
            sx={{ color: "#6c757d", textAlign: "right", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", p: 0 }}
          >
            registrar aceite sem mexer na agenda
          </Typography>
        )}
        </Box>
        )}
      </Box>
    );
  };

  const renderCard = (card: VacancyCard) => {
    const rec = card.recommended;
    const typeStyle = rec ? TYPE_COLORS[rec.type] : null;
    return (
      <Paper key={card.vacancyId} className="opportunity-card" elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {rec && typeStyle && (
              <Chip label={OPPORTUNITY_TYPE_LABELS[rec.type]} size="small" sx={{ backgroundColor: typeStyle.bg, color: typeStyle.fg, fontWeight: 700 }} />
            )}
            {card.value != null && card.value > 0 && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{money(card.value)} em risco</Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              title="Identificador desta oportunidade"
              sx={{ fontFamily: "monospace", color: colors.textSecondary, bgcolor: "#f1f3f5", px: 0.75, py: 0.25, borderRadius: "4px" }}
            >
              {opCode(card.number)}
            </Typography>
          </Box>
          {rec && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>score {Math.round(rec.score)}</Typography>
          )}
        </Box>

        <Typography sx={{ fontWeight: 700, color: colors.textPrimary, mt: 0.5 }}>
          {weekdayOf(card.startsAt)} {fmtDay(card.startsAt)} · {fmtTime(card.startsAt)}
          {card.professionalName ? ` · ${card.professionalName}` : ""}
          {card.serviceName ? ` — ${card.serviceName}` : ""}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", mb: 1 }}>
          Vaga por cancelamento{card.sourcePatientName ? ` (${card.sourcePatientName})` : ""}.
        </Typography>

        {/* RN-409b: vaga que saiu da fila mostra o desfecho e o caminho de volta */}
        {(card.status === "filled" || card.status === "dismissed") && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: "6px", p: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {card.status === "filled" ? "Resolvida" : "Descartada"}
              {rec?.actedAt ? ` em ${fmtDay(rec.actedAt)} ${fmtTime(rec.actedAt)}` : ""}
              {card.status === "filled" && rec ? ` — aceite de ${rec.patientName}` : ""}
              {!card.canReopen ? " · horário já passou, não é mais reabrível" : ""}
            </Typography>
            {card.canReopen && (
              <Button
                className="no-print"
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => setReopenTarget({ card })}
                startIcon={<Undo />}
                sx={{ textTransform: "none", color: "#0d6e6e", borderColor: "#0d6e6e" }}
              >
                Reabrir
              </Button>
            )}
          </Box>
        )}

        {rec ? (
          renderOpportunity(card, rec, true)
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.9rem", color: colors.textSecondary }}>
              Sem candidato elegível no momento — a vaga segue visível para tratamento manual.
            </Typography>
            {(card.status === "open" || card.status === "held") && (
              <Button className="no-print" size="small" variant="outlined" disabled={busy} onClick={() => setDismissTarget(card)} sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>
                Descartar vaga
              </Button>
            )}
          </Box>
        )}

        {card.alternatives.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {/* Destaque: é a ação mais útil do card quando o 1º candidato não atende, e passava
                despercebida como texto cinza. */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpanded((p) => ({ ...p, [card.vacancyId]: !p[card.vacancyId] }))}
              endIcon={expanded[card.vacancyId] ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: "none",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "#0d6e6e",
                borderColor: "#0d6e6e",
                backgroundColor: "#e8f4f4",
                "&:hover": { backgroundColor: "#d5eaea", borderColor: "#0a5757" },
              }}
            >
              {expanded[card.vacancyId] ? "Ocultar" : "Ver"} {card.alternatives.length} candidato{card.alternatives.length > 1 ? "s" : ""} alternativo{card.alternatives.length > 1 ? "s" : ""} para esta vaga
            </Button>
            <Collapse in={!!expanded[card.vacancyId]}>
              {card.alternatives.map((o) => renderOpportunity(card, o, false))}
            </Collapse>
          </Box>
        )}
      </Paper>
    );
  };

  // Rótulos dos caminhos da visão única: tipos de oferta + os dois fechos do funil
  const RECOVERY_PATH_LABELS: Record<string, string> = {
    ...OPPORTUNITY_TYPE_LABELS,
    outside: "Preenchida por fora (sync)",
    reposicao_registrada: "Reposição registrada (combinada ou reagendada)",
    reposicao_detectada: "Reposição detectada (sessão extra pelo sync)",
  };

  const renderReport = () => {
    if (!report) return null;
    // Visão única Perdas × Recuperações (pedido do Hilton, 21/08): vagas e reposições
    // no MESMO funil — os números do topo, do gráfico e da tabela fecham entre si.
    const losses = report.lossesTotal30d ?? report.vacanciesLast30d;
    const recovered = report.recoveredTotal30d ?? report.filledLast30d;
    const maxWeekly = Math.max(1, ...report.weekly.map((w) => w.lossesTotal ?? w.opened));
    const kpis = [
      { value: losses, label: "Perdas no período (vagas + reposições)" },
      { value: recovered, label: "Recuperadas no período" },
      { value: `${report.recoveryRate30d ?? report.reoccupationRate}%`, label: "Taxa de recuperação geral" },
      { value: money(report.revenueRecovered30d ?? report.revenueRecoveredThisMonth), label: "Receita recuperada no período" },
    ];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Filtro de período (relógio da entidade, inclusivo) — default últimos 30 dias */}
        <Box className="no-print" sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            label="De"
            type="date"
            size="small"
            value={reportFrom}
            onChange={(e) => e.target.value && setReportFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={reportTo}
            onChange={(e) => e.target.value && setReportTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            Perdas contadas pela data de abertura; recuperações pela data em que aconteceram.
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
          {kpis.map((k, i) => (
            <Paper key={i} elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: colors.textPrimary }}>{k.value}</Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{k.label}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2 }}>
          <Paper elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 2 }}>Perdas × recuperadas (por semana)</Typography>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, height: 180, borderBottom: "2px solid #e9ecef", pb: 1, overflowX: "auto" }}>
              {report.weekly.map((w, i) => {
                const wLosses = w.lossesTotal ?? w.opened;
                const wRecovered = w.recoveredTotal ?? w.filled;
                return (
                  <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: 56 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 130 }}>
                      <Box title={`perdas: ${wLosses}`} sx={{ width: 18, height: `${(wLosses / maxWeekly) * 100}%`, minHeight: wLosses > 0 ? 6 : 2, backgroundColor: "#ced4da", borderRadius: "3px 3px 0 0" }} />
                      <Box title={`recuperadas: ${wRecovered}`} sx={{ width: 18, height: `${(wRecovered / maxWeekly) * 100}%`, minHeight: wRecovered > 0 ? 6 : 2, backgroundColor: "#0d6e6e", borderRadius: "3px 3px 0 0" }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>{w.weekLabel}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>■ Perdas (vagas + reposições)</Typography>
              <Typography variant="caption" sx={{ color: "#0d6e6e" }}>■ Recuperadas</Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary, ml: "auto" }}>
                Tempo mediano até preencher vaga: {report.medianHoursToFill != null ? `${report.medianHoursToFill}h` : "—"}
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Recuperação por caminho (no período)</Typography>
            {(report.paths ?? []).length === 0 && (
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>Sem recuperações no período.</Typography>
            )}
            {(report.paths ?? []).map((p) => (
              <Box key={p.path} sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #f1f3f5" }}>
                <Typography sx={{ fontSize: "0.9rem" }}>{RECOVERY_PATH_LABELS[p.path] ?? p.path}</Typography>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                  {p.recovered} de {p.denominator}{p.denominator > 0 ? ` · ${p.rate}%` : ""}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
              <Typography sx={{ fontSize: "0.9rem" }}>Ofertas paradas há mais de 24h</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: report.staleCount > 0 ? "#dc3545" : colors.textPrimary }}>
                {report.staleCount}
              </Typography>
            </Box>
            {/* Nota honesta: a soma dos caminhos de vaga + reposição = recuperadas do topo */}
            <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", pt: 1 }}>
              "Preenchida por fora" é a vaga ocupada direto na agenda, sem passar pelo aceite — o sync detecta e fecha.
              "Reposição registrada" é a combinada na tela ou reagendada pela agenda; "detectada" é a sessão extra
              reconhecida pelo sync — as regras estão na aba Reposições.
            </Typography>
          </Paper>
        </Box>

        {/* RN-409f: o relatório presta contas também do lado do paciente (reposições) */}
        <Paper elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Reposições — cancelamentos feitos pelo profissional (no período)</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {[
              { value: report.repositionsCreated30d ?? 0, label: "Reposições surgidas", color: colors.textPrimary },
              { value: report.repositionsResolved30d ?? 0, label: "Repostas (registradas + detectadas)", color: "#0f5132" },
              { value: report.repositionsDismissed30d ?? 0, label: "Recusadas ou dispensadas", color: colors.textPrimary },
              { value: report.repositionsPendingNow ?? 0, label: "Pendentes agora", color: (report.repositionsPendingNow ?? 0) > 0 ? "#dc3545" : colors.textPrimary },
            ].map((k, i) => (
              <Box key={i} sx={{ border: "1px solid #f1f3f5", borderRadius: "8px", p: 1.5 }}>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: k.color }}>{k.value}</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>{k.label}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 portrait; margin: 10mm; }
          /* cada vaga é uma unidade: não pode partir entre páginas */
          .opportunity-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border: 1px solid #dee2e6 !important; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Oportunidades de agenda
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Vagas ranqueadas — trabalhe de cima para baixo. Cada card é uma vaga com o melhor candidato; registre o resultado de todo contato.
              </Typography>
              {/* Transparência das janelas do motor (pedido do Hilton, 03/08/2026) */}
              {queue?.vacancyWindowEnd && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, pb: "15px", mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    <strong>Vagas consideradas:</strong> cancelamentos com horário de hoje até{" "}
                    {fmtFullDay(queue.vacancyWindowEnd)} — janela de {queue.vacancyWindowDays} dias.
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    <strong>Candidatos ao encaixe:</strong> pacientes com sessão marcada de hoje até{" "}
                    {fmtFullDay(queue.candidateHorizonEnd)} — horizonte de {queue.candidateHorizonDays} dias.
                    Além desse prazo a antecipação é longa demais para convencer a família.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box className="no-print" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading}
                sx={{ textTransform: "none", backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5c636a" } }}
              >
                Imprimir
              </Button>
              {/* A aba Reposições ainda não tem planilha — o botão some para não prometer o que não faz */}
              {tab !== 1 && (
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  onClick={handleExport}
                  disabled={loading || exporting}
                  sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
                >
                  {exporting ? "Exportando..." : "Exportar planilha"}
                </Button>
              )}
              <FaqButton />
            </Box>
          </Box>

          {/* Abas, filtros e cards dentro do mesmo painel do título (Padrão Design item 4) */}
          <div className="c4u-panel">
          {/* Só na impressão: registra a aba e os filtros que geraram o papel */}
          <Typography className="print-only" variant="body2" sx={{ display: "none", mb: 1, color: colors.textSecondary }}>
            {tab === 0
              ? `Vagas a preencher — tipo: ${typeFilter === "all" ? "todas" : OPPORTUNITY_TYPE_LABELS[typeFilter as OpportunityType]} · profissional: ${professionalFilter === "all" ? "todos" : professionalFilter}`
              : tab === 1
                ? "Reposições — sessões canceladas pelo profissional"
                : "Relatório de oportunidades"}
            {` · gerado em ${new Date().toLocaleString("pt-BR")}`}
          </Typography>

          <Tabs className="no-print" value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none" } }}>
            <Tab label={`Vagas a preencher${queue ? ` (${queue.openCount})` : ""}`} />
            <Tab label={`Reposições${repositions ? ` (${repositions.openCount})` : ""}`} />
            <Tab label="Relatório" />
          </Tabs>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : tab === 2 ? (
            renderReport()
          ) : tab === 1 ? (
            renderRepositions()
          ) : (
            <>
              <Box className="no-print" sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, alignItems: "center" }}>
                {(["all", "holiday_relocation", "professional_cancellation", "double_session", "anticipation"] as const).map((t) => (
                  <Chip
                    key={t}
                    label={`${t === "all" ? "Todas" : OPPORTUNITY_TYPE_LABELS[t]} (${typeCounts[t] ?? 0})`}
                    onClick={() => setTypeFilter(t)}
                    sx={{
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: typeFilter === t ? "#212529" : "#fff",
                      color: typeFilter === t ? "#fff" : colors.textPrimary,
                      border: "1px solid #ced4da",
                      "&:hover": { backgroundColor: typeFilter === t ? "#212529" : "#f1f3f5" },
                    }}
                  />
                ))}
                <FormControl size="small" sx={{ minWidth: 170, ml: "auto" }}>
                  <InputLabel shrink>Profissional</InputLabel>
                  <Select label="Profissional" value={professionalFilter} onChange={(e) => setProfessionalFilter(e.target.value)} notched displayEmpty>
                    <MenuItem value="all">Todos</MenuItem>
                    {professionals.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* RN-409b: resgata o que saiu da fila por aceite, recusa ou descarte */}
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select
                    label="Situação"
                    value={situation}
                    onChange={(e) => setSituation(e.target.value as QueueSituation)}
                    notched
                    displayEmpty
                  >
                    <MenuItem value="open">Em aberto</MenuItem>
                    <MenuItem value="resolved">Resolvidas</MenuItem>
                    <MenuItem value="dismissed">Descartadas</MenuItem>
                    <MenuItem value="all">Todas</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {cards.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
                  <EventAvailable sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                  <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                    {situation === "open"
                      ? "Nenhuma vaga em aberto"
                      : situation === "resolved"
                        ? "Nenhuma vaga resolvida"
                        : situation === "dismissed"
                          ? "Nenhuma vaga descartada"
                          : "Nenhuma vaga"}
                  </Typography>
                  <Typography variant="body2">
                    {situation === "open"
                      ? "Cancelamentos futuros aparecem aqui em até 1 hora após o registro na agenda."
                      : "Troque a situação no filtro para ver as demais vagas."}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>{cards.map(renderCard)}</Box>
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      <ConfirmModal
        isOpen={dismissTarget !== null}
        title="Descartar vaga"
        message={`Descartar a vaga de ${dismissTarget ? `${fmtDay(dismissTarget.startsAt)} ${fmtTime(dismissTarget.startsAt)}` : ""}? Ela sai da fila e não será mais sugerida.`}
        type="warning"
        confirmButtonText="Descartar"
        cancelButtonText="Cancelar"
        isProcessing={busy}
        onConfirm={() => {
          if (!dismissTarget) return;
          act(() => opportunitiesService.dismissVacancy(dismissTarget.vacancyId), "Vaga descartada.");
          setDismissTarget(null);
        }}
        onCancel={() => setDismissTarget(null)}
      />

      <ConfirmModal
        isOpen={dismissRepositionTarget !== null}
        title="Dispensar reposição"
        message={`Dispensar a reposição de ${dismissRepositionTarget?.patientName ?? ""} (sessão de ${dismissRepositionTarget ? `${fmtDay(dismissRepositionTarget.lostStartsAt)} ${fmtTime(dismissRepositionTarget.lostStartsAt)}` : ""})? Ela sai da fila e a clínica deixa de dever esta sessão.`}
        type="warning"
        confirmButtonText="Dispensar"
        cancelButtonText="Cancelar"
        isProcessing={busy}
        onConfirm={() => {
          if (!dismissRepositionTarget) return;
          act(
            () => opportunitiesService.dismissReposition(dismissRepositionTarget.id, "dispensada pela equipe"),
            "Reposição dispensada."
          );
          setDismissRepositionTarget(null);
        }}
        onCancel={() => setDismissRepositionTarget(null)}
      />

      {/* RN-409b: reabrir exige motivo — ele fica no historico da oportunidade */}
      <Dialog
        open={reopenTarget !== null}
        onClose={busy ? undefined : () => { setReopenTarget(null); setReopenReason(""); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: "white", py: 1.5 }}>
          {reopenTarget?.offer ? "Reconsiderar candidato" : "Reabrir vaga"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            {!reopenTarget
              ? ""
              : reopenTarget.offer
                ? `${reopenTarget.offer.patientName} volta a ser sugerido para a vaga de ${fmtDay(reopenTarget.card.startsAt)} às ${fmtTime(reopenTarget.card.startsAt)}, e a pausa de 7 dias que a recusa gerou deixa de valer para ele.`
                : `A vaga de ${fmtDay(reopenTarget.card.startsAt)} às ${fmtTime(reopenTarget.card.startsAt)}${reopenTarget.card.professionalName ? ` com ${reopenTarget.card.professionalName}` : ""} volta para a fila com os candidatos que tinha. Se o horário já tiver sido ocupado na agenda, ela fecha sozinha no próximo ciclo.`}
          </Typography>
          <TextField
            label={reopenTarget?.offer ? "Motivo para reconsiderar" : "Motivo da reabertura"}
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            autoFocus
            error={reopenReason.trim().length === 0}
            helperText="Obrigatório — fica registrado no histórico (ex.: paciente desistiu depois, cliquei no card errado)."
            inputProps={{ maxLength: 200 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
          <Button
            onClick={() => { setReopenTarget(null); setReopenReason(""); }}
            disabled={busy}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!reopenTarget || reopenReason.trim().length === 0) return;
              const target = reopenTarget;
              const reason = reopenReason.trim();
              act(
                () =>
                  target.offer
                    ? opportunitiesService.reopenOpportunity(target.offer.id, reason)
                    : opportunitiesService.reopenVacancy(target.card.vacancyId, reason),
                target.offer
                  ? "Candidato de volta à fila desta vaga."
                  : "Vaga reaberta — ela volta para a fila com os candidatos."
              );
              setReopenTarget(null);
              setReopenReason("");
            }}
            disabled={busy || reopenReason.trim().length === 0}
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            Reabrir
          </Button>
        </DialogActions>
      </Dialog>

      {/* RN-409i: efetivação de aceite/reposição pelo próprio modal da agenda — a gravação
          passa pelos endpoints reais (RN-302 e todos os guards) e o resultado é registrado
          já vinculado ao agendamento */}
      <AppointmentModal
        isOpen={apptModalOpen}
        onClose={closeEffectuationModal}
        onSave={handleEffectuationSave}
        mode={apptModalMode}
        initialData={apptModalData}
        prefillOverrides={apptPrefill}
        businessHours={scheduleOptions?.businessHours}
        title={apptContext?.kind === "reposition"
          ? "Agendar reposição"
          : apptModalMode === "edit" ? "Efetivar reagendamento" : "Agendar na vaga"}
        patientsList={apptPatient ? [apptPatient.name] : []}
        disablePatientField
        professionalsList={(scheduleOptions?.professionals ?? []).filter((p) => p.active).map((p) => p.name)}
        teamsList={(scheduleOptions?.teams ?? []).map((t) => t.name)}
        serviceTypesList={effectuationServiceTypes.map((s) => s.name)}
        professionalOptions={(scheduleOptions?.professionals ?? []).filter((p) => p.active)}
        teamOptions={scheduleOptions?.teams ?? []}
        serviceTypeOptions={effectuationServiceTypes}
        loadPatientDiscounts={loadPatientDiscountsById}
        loadPatientInfo={loadPatientInfoByName}
        loadHolidaysOnDate={loadHolidaysOnDate}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadConflicts={({ professionalId, patient, from, to }) => schedulesService.findConflicts(
          professionalId,
          (apptPatient?.id ?? null),
          from, to)}
        confirmationOptions={(scheduleOptions?.confirmationStatuses ?? []).map((s) => s.name)}
        presenceOptions={(scheduleOptions?.presenceStatuses ?? []).map((s) => s.name)}
        terminalConfirmations={(scheduleOptions?.confirmationStatuses ?? [])
          .filter((s) => s.isTerminal).map((s) => s.name)}
        showPricing={scheduleOptions?.dataScope !== "own"}
        ownProfileLock={scheduleOptions?.dataScope === "own"}
        paid={apptModalMode === "edit" && (apptSchedule?.paid ?? false)}
        paymentDate={apptModalMode === "edit" ? apptSchedule?.paymentDate ?? null : null}
        paymentFinalValue={apptModalMode === "edit" ? apptSchedule?.paymentFinalValue ?? null : null}
        paymentDiscount={apptModalMode === "edit" ? apptSchedule?.paymentDiscount ?? null : null}
        locked={apptModalMode === "edit" && (apptSchedule?.locked ?? false)}
        rescheduleCount={apptModalMode === "edit" ? apptSchedule?.numReschedule ?? 0 : 0}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default Opportunities;
